import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, Beer, Truck, TrendingUp, AlertTriangle, CheckCircle,
  BookOpen, Droplets, Leaf, FlaskConical, Users, Share2,
  Thermometer, GitBranch, Brain, QrCode, CreditCard, Beaker,
  ShoppingBag, BarChart3,
} from 'lucide-react';
import { apiClient } from '@/lib/api-client';

interface HealthStatus {
  status: string;
  app: string;
  version: string;
  environment: string;
}

export function Dashboard() {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [inventoryCount, setInventoryCount] = useState<string>('--');
  const [productCount, setProductCount] = useState<string>('--');
  const [deliveryCount, setDeliveryCount] = useState<string>('--');
  const [orderCount, setOrderCount] = useState<string>('--');
  const [lowStockCount, setLowStockCount] = useState<number>(0);
  const [overdueKegCount, setOverdueKegCount] = useState<number>(0);
  const [iotAlertCount, setIotAlertCount] = useState<number>(0);
  const [coldChainAlertCount, setColdChainAlertCount] = useState<number>(0);
  const [recentActivity, setRecentActivity] = useState<Array<{ action: string; time: string; type: string }>>([]);

  useEffect(() => {
    fetch('/health')
      .then((res) => res.json())
      .then((data) => setHealth(data))
      .catch(() => {});

    // Fetch real counts from backend
    apiClient.get('/api/v1/inventory?limit=500').then(r => {
      const data = r.data?.data || [];
      setInventoryCount(String(data.length));
      const lowStock = data.filter((item: any) => item.quantity <= (item.reorder_point || 0)).length;
      setLowStockCount(lowStock);
    }).catch(() => setInventoryCount('0'));

    apiClient.get('/api/v1/products?limit=500').then(r => {
      const data = r.data?.data || [];
      setProductCount(String(data.length));
    }).catch(() => setProductCount('0'));

    apiClient.get('/api/v1/deliveries?limit=500').then(r => {
      const data = r.data?.data || [];
      setDeliveryCount(String(data.length));
      // Build recent activity from deliveries
      const activities: Array<{ action: string; time: string; type: string }> = [];
      data.slice(0, 3).forEach((d: any) => {
        activities.push({
          action: `Delivery #${d.id?.slice(0, 8) || '?'} - ${d.status || 'created'}`,
          time: d.created_at ? new Date(d.created_at).toLocaleDateString() : 'recently',
          type: 'delivery',
        });
      });
      setRecentActivity(prev => [...activities, ...prev].slice(0, 5));
    }).catch(() => setDeliveryCount('0'));

    apiClient.get('/api/v1/brewing-orders?limit=500').then(r => {
      const data = r.data?.data || [];
      setOrderCount(String(data.length));
      const activities: Array<{ action: string; time: string; type: string }> = [];
      data.slice(0, 2).forEach((o: any) => {
        activities.push({
          action: `Brewing order: ${o.recipe_name || o.id?.slice(0, 8) || '?'}`,
          time: o.created_at ? new Date(o.created_at).toLocaleDateString() : 'recently',
          type: 'order',
        });
      });
      setRecentActivity(prev => [...prev, ...activities].slice(0, 5));
    }).catch(() => setOrderCount('0'));

    apiClient.get('/api/v1/kegs?limit=500').then(r => {
      const data = r.data?.data || [];
      const overdue = data.filter((k: any) => k.status === 'overdue' || k.days_out > 30).length;
      setOverdueKegCount(overdue);
    }).catch(() => {});

    // Fetch IoT device alerts
    apiClient.get('/api/v1/iot/devices').then(r => {
      const devices = r.data?.data || [];
      const alerting = devices.filter((d: any) => d.status === 'alert' || d.status === 'error').length;
      setIotAlertCount(alerting);
    }).catch(() => {});

    // Fetch cold chain alerts
    apiClient.get('/api/v1/cold-chain/alerts/active').then(r => {
      const alerts = r.data?.data || [];
      setColdChainAlertCount(alerts.length);
    }).catch(() => {});
  }, []);

  const stats = [
    { title: 'Inventory Items', value: inventoryCount, change: 'Real-time from database', icon: Package, color: 'blue', link: '/inventory' },
    { title: 'Products', value: productCount, change: 'Active in catalog', icon: Beer, color: 'amber', link: '/products' },
    { title: 'Deliveries', value: deliveryCount, change: 'Total delivery orders', icon: Truck, color: 'green', link: '/distribution' },
    { title: 'Brewing Orders', value: orderCount, change: 'Contract brewing', icon: TrendingUp, color: 'purple', link: '/contract-brewing' },
  ];

  const modules = [
    { name: 'Inventory', icon: Package, href: '/inventory', desc: 'Track stock & materials', color: 'bg-blue-500' },
    { name: 'Kegs', icon: Beer, href: '/kegs', desc: 'Keg registry & tracking', color: 'bg-amber-500' },
    { name: 'Distribution', icon: Truck, href: '/distribution', desc: 'Orders & deliveries', color: 'bg-green-500' },
    { name: 'Products', icon: BarChart3, href: '/products', desc: 'Product catalog', color: 'bg-indigo-500' },
    { name: 'Recipes', icon: BookOpen, href: '/recipes', desc: 'Recipe builder & calc', color: 'bg-orange-500' },
    { name: 'Water Chemistry', icon: Droplets, href: '/water', desc: 'Water profiles & salts', color: 'bg-cyan-500' },
    { name: 'Hops Database', icon: Leaf, href: '/hops', desc: 'Hop profiles & compare', color: 'bg-lime-600' },
    { name: 'Ingredients', icon: FlaskConical, href: '/ingredients', desc: 'Malts, yeasts & more', color: 'bg-teal-500' },
    { name: 'Community', icon: Users, href: '/community', desc: 'Brewer social network', color: 'bg-pink-500' },
    { name: 'Collaboration', icon: Share2, href: '/collaboration', desc: 'Shared brew sessions', color: 'bg-violet-500' },
    { name: 'IoT Monitoring', icon: Thermometer, href: '/monitoring', desc: 'Temperature & sensors', color: 'bg-red-500' },
    { name: 'Workflows', icon: GitBranch, href: '/workflows', desc: 'Automate processes', color: 'bg-gray-600' },
    { name: 'AI Assistant', icon: Brain, href: '/ai', desc: 'Suggestions & forecasts', color: 'bg-purple-500' },
    { name: 'QR Tracking', icon: QrCode, href: '/qr-tracking', desc: 'Scan & track kegs', color: 'bg-emerald-500' },
    { name: 'Contract Brewing', icon: Beer, href: '/contract-brewing', desc: 'Brewing orders & QA', color: 'bg-yellow-600' },
    { name: 'R&D Center', icon: Beaker, href: '/rnd', desc: 'Experiments & trials', color: 'bg-sky-500' },
    { name: 'OTA Brew Pub', icon: Beer, href: '/ota', desc: 'Taproom management', color: 'bg-rose-500' },
    { name: 'Merchandise', icon: ShoppingBag, href: '/merchandise', desc: 'Merch store & orders', color: 'bg-fuchsia-500' },
    { name: 'Billing', icon: CreditCard, href: '/billing', desc: 'Plans & subscriptions', color: 'bg-slate-600' },
    { name: 'Analytics', icon: BarChart3, href: '/analytics', desc: 'Reports & insights', color: 'bg-indigo-600' },
  ];

  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome to BrewFlow - Your complete brewery management platform</p>
      </div>

      {health && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">System: <span className="font-medium capitalize">{health.status}</span></span>
              </div>
              <div className="text-gray-500">v{health.version}</div>
              <div className="text-gray-500 capitalize">{health.environment}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-700">Online</span>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.title}
              to={stat.link}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-lg ${colorClasses[stat.color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600">{stat.title}</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-1">{stat.change}</p>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {(recentActivity.length > 0 ? recentActivity : [
              { action: 'No recent activity yet', time: 'Start by adding data', type: 'info' },
            ]).map((activity, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5">
                <div className={`w-2 h-2 rounded-full ${activity.type === 'alert' ? 'bg-orange-500' : 'bg-green-500'}`} />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{activity.action}</p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Active Alerts</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-900">Low Stock Items</p>
                <p className="text-xs text-orange-700 mt-0.5">{lowStockCount} items below reorder point</p>
                <Link to="/inventory" className="text-xs text-orange-600 hover:text-orange-800 font-medium mt-1 inline-block">View Inventory</Link>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-900">Overdue Kegs</p>
                <p className="text-xs text-red-700 mt-0.5">{overdueKegCount} kegs overdue for return</p>
                <Link to="/kegs" className="text-xs text-red-600 hover:text-red-800 font-medium mt-1 inline-block">View Kegs</Link>
              </div>
            </div>
            {coldChainAlertCount > 0 && (
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Thermometer className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-purple-900">Cold Chain Alerts</p>
                  <p className="text-xs text-purple-700 mt-0.5">{coldChainAlertCount} active temperature alert{coldChainAlertCount !== 1 ? 's' : ''}</p>
                  <Link to="/monitoring" className="text-xs text-purple-600 hover:text-purple-800 font-medium mt-1 inline-block">View Monitoring</Link>
                </div>
              </div>
            )}
            {iotAlertCount > 0 && (
              <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">IoT Device Alerts</p>
                  <p className="text-xs text-yellow-700 mt-0.5">{iotAlertCount} device{iotAlertCount !== 1 ? 's' : ''} in alert state</p>
                  <Link to="/monitoring" className="text-xs text-yellow-600 hover:text-yellow-800 font-medium mt-1 inline-block">View Devices</Link>
                </div>
              </div>
            )}
            {coldChainAlertCount === 0 && iotAlertCount === 0 && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-900">All Systems Operational</p>
                  <p className="text-xs text-blue-700 mt-0.5">IoT sensors, cold chain, and services running normally</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* All Modules Grid */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">All Modules</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {modules.map((mod) => {
            const Icon = mod.icon;
            return (
              <Link
                key={mod.href}
                to={mod.href}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all group"
              >
                <div className={`w-9 h-9 ${mod.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm font-medium text-gray-900">{mod.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{mod.desc}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
